import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CurrencyDollar } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export default function ForgotPasswordPage() {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setSubmitting(true);

    try {
      const response = await forgotPassword(email);
      setMessage(response?.message || 'If the email exists, a password reset link has been sent.');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center p-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8 animate-fade-in">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-[#1E3F2D] rounded-2xl mb-4">
            <CurrencyDollar size={28} weight="bold" className="text-white" />
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight text-[#1C1B1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Forgot Password
          </h1>
          <p className="text-[#5C5A56] mt-1 text-sm">Enter your email and we will send a reset link.</p>
        </div>

        <div className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-8 animate-fade-in">
          {message && (
            <div className="bg-green-50 border border-green-200 text-[#265C42] text-sm rounded-lg p-3 mb-4">
              {message}
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-[#A63C24] text-sm rounded-lg p-3 mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="forgot-email" className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-2">Email</label>
              <input
                id="forgot-email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20 focus:border-[#1E3F2D] transition-all"
                placeholder="you@example.com"
                required
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#1E3F2D] text-white font-semibold rounded-xl px-6 py-3 hover:bg-[#29563E] transition-colors active:scale-[0.98] disabled:opacity-60"
            >
              {submitting ? 'Sending link...' : 'Send Reset Link'}
            </button>
          </form>

          <p className="text-center text-sm text-[#5C5A56] mt-6">
            Remembered your password?{' '}
            <Link to="/login" className="text-[#1E3F2D] font-semibold hover:underline">
              Back to Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
